// contexts/organization.ctx.tsx
import { createContext, useContext, useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '@/contexts/auth.ctx';
// import { CacheManager } from '@/utils/cache.man.tsx';
import type { Organization } from 'pc/client.ts';

interface OrganizationContextType {
    organization: Organization | null;
    orgPlan: { history: string, seatLimit: number, profileEach: number } | null;
    orgStats: { activeSeats: number, invitedSeats: number, testsIn30: number, totalTeams: number, soloUsers: number } | null;
    accessorList: { id: string, name: string, email: string, role: string, testResult: string, compatibilityRatio: string, team?: string | null, testCounts: number[] }[] | null;
    inviteAccessor: (email: string, name?: string, team?: string) => Promise<boolean>;
}

const OrganizationLazyProvider = lazy(() => import('./org.ctx.tsx').then(mod => ({ default: mod.OrganizationProvider })));

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { authLevel, } = useAuth();
    const [organization,] = useState<Organization | null>(null);
    const [orgPlan, setOrgPlan] = useState<{ history: string, seatLimit: number, profileEach: number } | null>(null);
    const [orgStats, setOrgStats] = useState<{ activeSeats: number, invitedSeats: number, testsIn30: number, totalTeams: number, soloUsers: number } | null>(null);
    const [accessorList, setAccessorList] = useState<{ id: string, name: string, email: string, role: string, testResult: string, compatibilityRatio: string, team?: string | null, testCounts: number[] }[] | null>(null);

    // Only load organization data if user has organization access
    useEffect(() => {
        if (authLevel === 'MANAGER' || authLevel === 'YENTA') {
            loadOrganization();
        }
    }, [authLevel]);

    const loadOrganization = async () => {
        setOrgPlan({ history: '5-years', seatLimit: 5, profileEach: 2 });
        setOrgStats({ activeSeats: 2, invitedSeats: 1, testsIn30: 25, totalTeams: 1, soloUsers: 1 });
        setAccessorList([
            { id: '1', name: 'John Doe', email: 'john.doe@example.com', role: 'Manager', testResult: 'INTJ-T', compatibilityRatio: '15/18', team: null, testCounts: [34, 8, 2] },
            { id: '2', name: 'Jane Smith', email: 'jane.smith@example.com', role: 'Agent', testResult: 'ENFP-T', compatibilityRatio: '12/18', team: 'Team Win', testCounts: [12, 3, 1] }
        ]);
    }

    const inviteAccessor = async (email: string, name?: string, team?: string): Promise<boolean> => {
        console.log('inviting accessor', email, name, team);
        return false;
    }

    return (
        <OrganizationContext.Provider value={{ organization, orgPlan, orgStats, accessorList, inviteAccessor }}>
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};

export const ConditionalOrganizationProvider = ({ children }: { children: React.ReactNode }) => {
    const { authLevel } = useAuth();

    if (authLevel !== 'MANAGER' && authLevel !== 'YENTA') {
        console.log('ConditionalOrganizationProvider -- not a manager or yenta, skipping');
        return <>{children}</>;
    }

    return (
        <Suspense fallback={<div>Loading organization features...</div>}>
            <OrganizationLazyProvider>
                {children}
            </OrganizationLazyProvider>
        </Suspense>
    );
};